/**
 * AuthGuard — гейт между routing-уровнем и authenticated-частью MiniApp (Stage 5.X.2).
 *
 * Поведение:
 *
 * 1. **На mount** — если статус `idle`, автоматически вызывает `login()`
 *    через `Telegram.WebApp.initData`. Эта точка — единственная для
 *    инициализации сессии.
 * 2. **Во время loading** — рендерит `<LoadingFallback />` (skeleton-shimmer
 *    появится в Stage 5.X.3).
 * 3. **При error** — рендерит retry-экран с кнопкой «Попробовать снова».
 *    Юзер может вручную повторить — например, если сначала не было сети.
 * 4. **При authenticated** — рендерит `children`.
 *
 * Зачем компонент, а не `useEffect` где-то наверху:
 *   - Гарантирует, что children не рендерятся до получения JWT (защита от
 *     неавторизованных запросов из лениво-загружаемых страниц).
 *   - Однозначное место для UX-сообщений auth-ошибок (не разбросаны по
 *     5 страницам).
 *   - Легко обернуть SSR/Storybook в `<AuthGuard.Mock>` при необходимости.
 *
 * См. `apps/miniapp/src/App.tsx` для wiring.
 */

import { useEffect } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { notifyWebAppReady } from '@/lib/telegram';

import { LoadingFallback } from './LoadingFallback';

export interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps): JSX.Element {
  const { status, isAuthenticated, isLoading, error, login } = useAuth();

  // Auto-login at mount, only once when status === 'idle'.
  useEffect(() => {
    if (status === 'idle') {
      void login();
    }
  }, [status, login]);

  // Сообщить Telegram'у "WebApp готов" — убирает spinner поверх MiniApp.
  // Делаем это сразу, не дожидаясь auth: пользователь видит наш UI быстрее.
  useEffect(() => {
    notifyWebAppReady();
  }, []);

  if (isLoading || status === 'idle') {
    return <LoadingFallback />;
  }

  if (status === 'error') {
    return <AuthErrorScreen message={error} onRetry={() => void login()} />;
  }

  if (!isAuthenticated) {
    // Defensive fallback — теоретически unreachable, но защищает children
    // от рендера с пустым accessToken.
    return <LoadingFallback />;
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// AuthErrorScreen — экран retry при login-fail
// ---------------------------------------------------------------------------

interface AuthErrorScreenProps {
  message: string | null;
  onRetry: () => void;
}

function AuthErrorScreen({ message, onRetry }: AuthErrorScreenProps): JSX.Element {
  return (
    <main className="auth-error">
      <div className="glass-card auth-error__card">
        <h1 className="auth-error__title">Не удалось войти</h1>
        <p className="auth-error__message">
          {message ?? 'Произошла непредвиденная ошибка авторизации.'}
        </p>
        <button type="button" className="auth-error__retry" onClick={onRetry}>
          Попробовать снова
        </button>
      </div>
    </main>
  );
}
